import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@jaks/core/api-client";

interface Row { id: string; at: string; kind: string; title: string; body: string | null; href: string | null; readAt: string | null }

function useInbox() {
  return useQuery<{ notifications: Row[] }>({
    queryKey: ["notifications"],
    queryFn: () => api("/api/home/notifications"),
    staleTime: 55_000,
    refetchInterval: 60_000,
  });
}

function ago(iso: string): string {
  const min = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (min < 1) return "now";
  if (min < 60) return `${min}m`;
  if (min < 24 * 60) return `${Math.round(min / 60)}h`;
  return `${Math.round(min / (24 * 60))}d`;
}

export function Bell({ variant }: { variant: "sidebar" | "mobile" }) {
  const [open, setOpen] = useState(false);
  const inbox = useInbox();
  const qc = useQueryClient();
  const rows = inbox.data?.notifications ?? [];
  const unread = rows.filter((r) => !r.readAt).length;

  async function markAllRead() {
    await api("/api/home/notifications/read", { method: "POST", body: "{}" });
    qc.invalidateQueries({ queryKey: ["notifications"] });
  }

  return (
    <>
      <button
        className={variant === "mobile" ? "bell bell-mobile" : "bell"}
        aria-label={unread ? `Notifications, ${unread} unread` : "Notifications"}
        onClick={() => setOpen((v) => !v)}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {unread > 0 && <span className="bell-badge">{unread > 9 ? "9+" : unread}</span>}
      </button>
      {open && (
        <>
          <div className="bell-scrim" onClick={() => setOpen(false)} />
          <section className={"bell-panel" + (variant === "mobile" ? " from-mobile" : "")} aria-label="Notifications">
            <header>
              <b>Notifications</b>
              {unread > 0 && <button className="bell-mark" onClick={markAllRead}>Mark all read</button>}
              <button className="bell-close" aria-label="Close" onClick={() => setOpen(false)}>✕</button>
            </header>
            <div className="bell-list">
              {rows.length === 0 && <div className="bell-empty">Nothing yet. Approvals, exports, and alerts land here.</div>}
              {rows.map((r) => {
                const inner = (
                  <>
                    <div className="bell-row-head">
                      {!r.readAt && <span className="bell-dot" aria-label="Unread" />}
                      <b>{r.title}</b>
                      <time>{ago(r.at)}</time>
                    </div>
                    {r.body && <p>{r.body}</p>}
                  </>
                );
                return r.href ? (
                  <a key={r.id} className="bell-row" href={r.href} target={r.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" onClick={() => setOpen(false)}>
                    {inner}
                  </a>
                ) : (
                  <div key={r.id} className="bell-row">{inner}</div>
                );
              })}
            </div>
          </section>
        </>
      )}
    </>
  );
}
